import React, { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useApiResource } from "../api/useApi";
import { getSettings } from "../api/resources";
import { NEXT_FEATURES } from "../featureFlags";
import {
  ATHLETES,
  GLOBAL_ORG_ID,
  Org,
  OrgRole,
  ORG_INTEGRATIONS,
  ROLE_LABEL,
  SHARE_SCOPES,
  ShareScope,
  isOrgManager,
  planLabel,
} from "../mockData";
import { IntegrationGrid, ApiCodeBlock } from "../components/IntegrationGrid";
import { PageTabs } from "../components/PageTabs";
import {
  Building2,
  Users,
  CreditCard,
  Palette,
  Wrench,
  Crown,
  Plus,
  ArrowRight,
  Lock,
  Workflow,
} from "lucide-react";

type Tab = "general" | "members" | "billing" | "integrations" | "branding" | "defaults";

const ALL_TABS = [
  { key: "general",      label: "General",         icon: Building2 },
  { key: "members",      label: "Members",         icon: Users },
  { key: "billing",      label: "Plan & billing",  icon: CreditCard },
  { key: "integrations", label: "Integrations",    icon: Workflow },
  { key: "branding",     label: "Branding",        icon: Palette },
  { key: "defaults",     label: "Capture defaults", icon: Wrench },
];

// Single-org release 1: only General + Capture defaults are wired. The rest
// (Members / Billing / Branding / Integrations) need the multi-org backend
// and stay hidden until NEXT_FEATURES is on.
const TABS = NEXT_FEATURES
  ? ALL_TABS
  : ALL_TABS.filter((t) => t.key === "general" || t.key === "defaults");

export default function Settings() {
  const { org, role } =
    useOutletContext<{ org: Org; role: OrgRole }>();

  const [tab, setTab] = useState<Tab>("general");
  const inGlobal = org.id === GLOBAL_ORG_ID;
  const canManage = isOrgManager(role) && !inGlobal;

  // Pre-load org settings; updateSettings is wired in the form's onSave
  // once the backend DTO exposes the fields rendered below.
  useApiResource((apiFetch) => getSettings(apiFetch), []);

  // The role / global-org "upgrade" wall only makes sense once we have
  // multi-org. In single-org release 1 we just show the slim settings.
  if (NEXT_FEATURES && inGlobal) {
    return <LockedState reason="global" />;
  }

  if (NEXT_FEATURES && !canManage) {
    return <LockedState reason="role" orgName={org.name} />;
  }

  return (
    <>
      <PageTabs tabs={TABS} value={tab} onChange={(v) => setTab(v as Tab)} />

      <div className="space-y-4">
        {/* Compact org context strip — multi-org affordance, hidden in v1 */}
        {NEXT_FEATURES && (
          <div className="card p-4 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-semibold text-slate-900">{org.name}</div>
              <span className="badge bg-el-50 text-el-700 ring-1 ring-el-200">
                <Crown className="h-3 w-3" /> {ROLE_LABEL[role]}
              </span>
              <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                {planLabel(org.plan)} plan
              </span>
            </div>
            <div className="text-[0.7rem] text-slate-500 ml-auto">
              Personal preferences live under{" "}
              <Link to="/profile" className="text-el-700 hover:text-el-800 font-medium">Profile</Link>.
            </div>
          </div>
        )}

        <div className="card p-5">
          {tab === "general"      && <GeneralTab      org={org} />}
          {tab === "members"      && <MembersTab      org={org} />}
          {tab === "billing"      && <BillingTab      org={org} />}
          {tab === "integrations" && <IntegrationsTab org={org} />}
          {tab === "branding"     && <BrandingTab     org={org} />}
          {tab === "defaults"     && <DefaultsTab     org={org} />}
        </div>
      </div>
    </>
  );
}

function LockedState({ reason, orgName }: { reason: "global" | "role"; orgName?: string }) {
  return (
    <div className="space-y-4">
      <div className="card p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-slate-100 text-slate-600 ring-1 ring-slate-200 flex items-center justify-center">
            <Lock className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">
              {reason === "global" ? "Organisation settings are not available in Eleetz Global" : "You don't manage this organisation"}
            </div>
            <p className="text-xs text-slate-600 mt-0.5 max-w-2xl">
              {reason === "global" ? (
                <>
                  Eleetz Global is the shared default home — there's nothing to configure here. To
                  manage an organisation, create your own or accept an invite.
                </>
              ) : (
                <>
                  Only owners and coaches can edit settings for <span className="font-medium text-slate-800">{orgName}</span>. Your personal preferences are under{" "}
                  <Link to="/profile" className="text-el-700 hover:text-el-800 font-medium">Profile</Link>.
                </>
              )}
            </p>
          </div>
          {reason === "global" && (
            <button className="btn-primary !h-9">
              Upgrade <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {reason === "global" && (
        <div className="card p-5 bg-gradient-to-br from-el-50/60 to-white">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-el-100 text-el-700 flex items-center justify-center">
              <Crown className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">What you get with a paid org</div>
              <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1.5 text-xs text-slate-600">
                <Bullet>Private roster of athletes / clients</Bullet>
                <Bullet>Branded AI reports and custom domain</Bullet>
                <Bullet>Per-coach seats with role permissions</Bullet>
                <Bullet>Org-wide AMS, SSO and webhook integrations</Bullet>
                <Bullet>Billing + invoicing with VAT support</Bullet>
                <Bullet>14-day free trial — your sessions stay yours</Bullet>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-1.5">
      <span className="mt-1.5 h-1 w-1 rounded-full bg-el-500 shrink-0" />
      {children}
    </li>
  );
}


function Field({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <label className="block">
      <div className="label-eyebrow">{label}</div>
      <input
        defaultValue={value}
        className="mt-1 w-full h-9 px-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
      />
      {hint && <div className="text-[0.7rem] text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}

function ScopePicker({ defaultScope }: { defaultScope: ShareScope }) {
  const [scope, setScope] = useState<ShareScope>(defaultScope);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {SHARE_SCOPES.map((s) => (
        <button
          key={s.id}
          onClick={() => setScope(s.id)}
          className={[
            "text-left rounded-md border p-3 transition",
            scope === s.id ? "border-el-300 bg-el-50/60 ring-1 ring-el-200" : "border-slate-200 hover:border-el-200",
          ].join(" ")}
        >
          <div className="text-sm font-semibold text-slate-900">{s.label}</div>
          <div className="text-[0.7rem] text-slate-500 mt-0.5">{s.hint}</div>
        </button>
      ))}
    </div>
  );
}

/* ---------- org tabs ---------- */

function GeneralTab({ org }: { org: Org }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Organisation name" value={org.name} />
      <Field label="Type"              value={org.type} hint="Academy · Club · Clinic · Studio" />
      <Field label="Primary activity"     value="Golf" />
      <Field label="Time zone"         value="Europe/London" />
      <Field label="Default share scope" value="Organisation" />
      <Field label="Public profile URL"  value={`eleetz.app/${org.id}`} />
    </div>
  );
}

function MembersTab({ org }: { org: Org }) {
  const sample = ATHLETES.slice(0, 6);
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500">{org.athletes.toLocaleString()} total · showing first 6</div>
        <button className="btn-primary !h-8 !text-xs">
          <Plus className="h-3.5 w-3.5" /> Invite member
        </button>
      </div>
      <div className="rounded-md border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] uppercase tracking-wide text-slate-500 bg-slate-50/60 border-b border-slate-100">
              <th className="px-4 py-2 font-semibold">Name</th>
              <th className="px-4 py-2 font-semibold">Sport</th>
              <th className="px-4 py-2 font-semibold">Role</th>
              <th className="px-4 py-2 font-semibold">Sessions</th>
              <th className="px-4 py-2 font-semibold w-20"></th>
            </tr>
          </thead>
          <tbody>
            {sample.map((a, i) => (
              <tr key={a.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-el-200 to-el-400 text-white text-[0.65rem] font-semibold flex items-center justify-center">
                      {a.initials}
                    </div>
                    <span className="text-slate-800">{a.name}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-slate-600">{a.activity}</td>
                <td className="px-4 py-2.5">
                  <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                    {i === 0 ? "Owner" : i === 1 ? "Coach" : "Athlete"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-slate-600">{a.sessions}</td>
                <td className="px-4 py-2.5 text-right">
                  <button className="btn-ghost !h-7 !text-xs">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BillingTab({ org }: { org: Org }) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500">Current plan</div>
            <div className="mt-0.5 text-base font-semibold text-slate-900">{planLabel(org.plan)}</div>
          </div>
          <button className="btn-secondary !h-9">Change plan</button>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <Mini label="Seats used"          value="14 / 50" />
          <Mini label="Sessions this month" value="1,284" />
          <Mini label="Storage"             value="42 GB" />
        </div>
      </div>

      <div className="rounded-md border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500">Payment method</div>
            <div className="mt-0.5 text-sm text-slate-800">Visa ·· 4242 — exp 06/29</div>
          </div>
          <button className="btn-ghost !h-8 !text-xs">Update</button>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-semibold text-slate-600">
          Invoices
        </div>
        <ul className="divide-y divide-slate-100">
          {["2026-04-01", "2026-03-01", "2026-02-01"].map((d) => (
            <li key={d} className="px-4 py-2.5 flex items-center justify-between text-sm">
              <span className="text-slate-700">{d} — Team plan, monthly</span>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">£249.00</span>
                <button className="btn-ghost !h-7 !text-xs">PDF</button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function IntegrationsTab({ org }: { org: Org }) {
  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500 max-w-2xl">
        Organisation connections apply to everyone in {org.name}. AMS exports, SSO, webhooks and
        custom storage live here.
      </div>
      <IntegrationGrid items={ORG_INTEGRATIONS} />
      <div className="border-t border-slate-100 pt-4">
        <div className="text-sm font-semibold text-slate-900 mb-1">REST API · {org.name}</div>
        <div className="text-xs text-slate-500 mb-3">
          Drive any integration that isn't yet first-class with the org's API key.
        </div>
        <ApiCodeBlock orgName={org.name} />
      </div>
    </div>
  );
}

function BrandingTab({ org }: { org: Org }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Display name on reports" value={org.name} />
      <Field label="Primary colour"          value="#10b981" />
      <Field label="Reply-to email"          value="coach@eleetz.app" />
      <Field label="Custom domain"           value="reports.eleetz.app" hint="Optional · CNAME to reports.eleetz.app" />
    </div>
  );
}

function DefaultsTab({ org }: { org: Org }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Primary activity"          value="Golf" hint="Org-wide landing emphasis" />
        <Field label="Default camera profile" value="Face-on" />
        <Field label="Frame rate"             value="60 fps" />
        <Field label="Auto-publish AI report" value="On" />
      </div>
      <div>
        <div className="label-eyebrow mb-2">Default share scope for org captures</div>
        <ScopePicker defaultScope="org" />
      </div>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2.5">
      <div className="text-[0.65rem] uppercase tracking-[0.06em] text-slate-500 font-semibold">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}
